import React from 'react';
import PropTypes from 'prop-types';
import {
  object, string,
} from 'yup';
import * as firebase from 'firebase/app';
import { Typography, Box, makeStyles } from '@material-ui/core';
import { FormattedMessage, useIntl } from 'react-intl';
import AuthPage from '../../page/AuthPage';
import MyForm from '../../form/MyForm';
import { MY_FORM_FIELD_TEXT } from '../../form/form-constants';
import { AppState } from '../../state/MyStateProvider';
import { FirebaseState } from '../MyFirebaseProvider';
import FirebaseRepository from '../FirebaseRepository';
import prepareFormikInitialValues from '../../form/form-helpers';
import SeedFirebaseSMSConfirmationDialog from './SeedFirebaseSMSConfirmationDialog';
import pageUtilsPropTypes from '../../page/page-prop-types';
import seedLogo from '../../images/logo.svg';
import { seedAppIcons } from '../../icon/seed-icons';

const useStyles = makeStyles((theme) => ({
  logoContainer: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: theme.spacing(2),
  },
  logo: {
    width: 200,
    height: 'auto',
  },
}));

const namePhoneNumber = 'phoneNumber';

export const fields = [
  {
    name: namePhoneNumber,
    type: MY_FORM_FIELD_TEXT,
    labelId: 'seed.firebase.authentication.form.phoneNumber',
    inputProps: {
      autoFocus: true,
      placeholder: '+62',
    },
  },
];

export const validationSchema = object({
  [namePhoneNumber]: string()
    .trim()
    .required('seed.firebase.authentication.form.phoneNumber.required'),
});

const SeedFirebaseLoginPhoneNumberPage = (props) => {
  const {
    pageUtils,
    logo,
    applicationName,
    icons,
  } = props;
  const classes = useStyles();
  const intl = useIntl();
  const { state, dispatch } = React.useContext(AppState);
  const { firebaseConfig } = React.useContext(FirebaseState);

  const [isLoading, setIsLoading] = React.useState(false);
  const [firebaseRepo] = React.useState(new FirebaseRepository(state, dispatch, firebaseConfig));

  const [openDialog, setOpenDialog] = React.useState(false);
  const [confirmationResult, setConfirmationResult] = React.useState(null);

  React.useEffect(() => {
    window.recaptchaVerifier = new firebase.auth.RecaptchaVerifier('my-form-submit-button', {
      size: 'invisible',
      callback: () => {
        // reCAPTCHA solved, allow signInWithPhoneNumber.
      },
    });
    return () => {

    };
  }, []);

  const handleSubmitLogin = (values) => {
    setIsLoading(true);
    pageUtils.loadSomethingSilent(
      () => firebaseRepo.signInPhoneNumber(values, window.recaptchaVerifier),
      (payload) => {
        setConfirmationResult(payload.confirmationResult);
        setOpenDialog(true);
        pageUtils.showSnackbar(
          intl.formatMessage({
            id: 'seed.firebase.authentication.loginPhone.success',
          }, {
            phoneNumber: values.phoneNumber,
          }), 'success',
        );
      },
      (error) => {
        if (error.message) {
          pageUtils.showSnackbar(error.message, 'error');
        } else {
          pageUtils.showSnackbar(intl.formatMessage({ id: 'seed.firebase.authentication.loginPhone.error' }), 'error');
        }
      },
      () => {
        setIsLoading(false);
      },
    );
  };

  const handleConfirmSMS = (values) => {
    setIsLoading(true);
    pageUtils.loadSomethingSilent(
      () => firebaseRepo.confirmSMSCode(confirmationResult, values),
      (payload) => {
        setConfirmationResult(payload.confirmationResult);
        setOpenDialog(true);
        // window.localStorage.setItem('emailForSignIn', email);
        // pageUtils.showSnackbar(`We have sent an email to ${email}`, 'success');
      },
      (error) => {
        if (error.message) {
          pageUtils.showSnackbar(error.message, 'error');
        } else {
          pageUtils.showSnackbar(error, 'error');
        }
      },
      () => {
        setIsLoading(false);
      },
    );
  };

  const initialValues = prepareFormikInitialValues(null, true, fields);

  return (
    <>
      <AuthPage isLoading={isLoading} logo={logo}>
        <div className={classes.logoContainer}>
          <img className={classes.logo} alt={`${applicationName} Logo`} src={logo} />
        </div>
        <Typography align="center" component="h1" variant="h5">
          <FormattedMessage id="seed.firebase.authentication.loginPhone.heading" values={{ applicationName }} />
        </Typography>
        <Box mt={6} />
        <MyForm
          fields={fields}
          validationSchema={validationSchema}
          onSubmit={handleSubmitLogin}
          disabled={isLoading}
          submitButtonLabelId="seed.firebase.authentication.loginPhone.button.submit"
          initialValues={initialValues}
          icons={icons}
        />
      </AuthPage>
      <SeedFirebaseSMSConfirmationDialog
        open={openDialog}
        isLoading={isLoading}
        onClose={() => setOpenDialog(false)}
        icons={icons}
        onSubmit={handleConfirmSMS}
      />
    </>
  );
};

SeedFirebaseLoginPhoneNumberPage.propTypes = {
  pageUtils: pageUtilsPropTypes.isRequired,
  logo: PropTypes.string,
  applicationName: PropTypes.string,
  icons: PropTypes.objectOf(PropTypes.object),

};

SeedFirebaseLoginPhoneNumberPage.defaultProps = {
  logo: seedLogo,
  applicationName: 'react-seed',
  icons: seedAppIcons,
};

export default SeedFirebaseLoginPhoneNumberPage;
